import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { ResponsiveDataList } from "../../components/responsive";
import DeleteModel from "../../components/DeleteModal";
import HelpApi from "../../api/helpApi";
import Helpers from "../../components/Helpers";
import TicketAccess from "./TicketAccess";
import MessagesModal from "./MessagesModal";
import { TicketListToolbar } from "./manage/TicketListToolbar";
import { TicketCard } from "./manage/cards/TicketCard";

const Tickets: React.FC = () => {
  const location = useLocation();
  // main data
  const [tickets, setTickets] = useState<any[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [LoadData, setLoadData] = useState<boolean>(false);

  // modal / selected
  const [show, setShow] = useState<boolean>(false);
  const [selectedTicket, setSelectedTicket] = useState<any>(null);

  // filters / search
  const [searchKey, setSearchKey] = useState<string>(
    () => (location.state as any)?.searchKeyword || "",
  );
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [priorityFilter, setPriorityFilter] = useState<string>("");

  // popup states
  const [showRolesPopUp, setShowRolesPopUp] = useState<boolean>(false);
  const [messagesOpen, setMessagesOpen] = useState<boolean>(false);
  const [newAssign, setNewAssign] = useState<boolean>(false);

  // pagination / sorting (kept simple — your CustomDataTable calls getAllTickets)
  const pageSize = 20;

  const helpApi = useRef(new HelpApi()).current;
  const searchDebounceRef = useRef<any>(null);

  useEffect(() => {
    getAllTickets();
    // cleanup on unmount
    return () => clearTimeout(searchDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderTooltip = (props: any) => (
    <Tooltip id="button-tooltip">{props}</Tooltip>
  );

  // fetcher
  const getAllTickets = async (
    pageNo: number = 1,
    size: number = pageSize,
    searchKeyword: string = searchKey,
    sortBy: Record<string, any> = {}
  ) => {
    setLoadData(true);
    const payload = {
      pageNo,
      size,
      searchKeyword,
      sortBy,
      filters: {
        category: categoryFilter,
        status: statusFilter,
        priority: priorityFilter,
      },
    };

    try {
      const res: any = await helpApi.getAllTickets(payload);
      if (res.success) {
        setTickets(res.data);
        setTotal(res.totalRecords || 0);
      } else {
        toast.error(res.msg || "Failed to fetch tickets");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch tickets");
    } finally {
      setLoadData(false);
    }
  };

  // Search with debounce to avoid too many requests
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchKey(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      getAllTickets(1, pageSize, value);
    }, 350);
  };

  // 👇 replace handleFilterChange
  const handleFilterChange = (type, value) => {
    if (type === "category") setCategoryFilter(value);
    if (type === "status") setStatusFilter(value);
    if (type === "priority") setPriorityFilter(value);
  };

  // 👇 replace clearFilters
  const clearFilters = () => {
    setCategoryFilter("");
    setStatusFilter("");
    setPriorityFilter("");
    setSearchKey("");
  };

  // 👇 add this useEffect (below useEffect for mount)
  useEffect(() => {
    getAllTickets(1, pageSize, searchKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryFilter, statusFilter, priorityFilter]);

  // modals and actions
  const handleShowDelete = (ticket) => {
    setSelectedTicket(ticket);
    setShow(true);
  };
  const handleClose = () => setShow(false);

  const deleteTicket = async () => {
    if (!selectedTicket) return;
    try {
      const res = await helpApi.deleteTicket({ ticket_id: selectedTicket.id });
      if (res.success) {
        toast.success(res.msg || "Ticket deleted");
        setShow(false);
        getAllTickets();
      } else {
        toast.error(res.msg || "Failed to delete");
      }
    } catch (err) {
      toast.error("Failed to delete ticket");
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await helpApi.updateStatus({
        id,
        status: newStatus,
      });
      toast.success("Ticket updated successfully");
      getAllTickets();
    } catch (error) {
      console.error("Update status error:", error);
    }
  };

  const rolesPopUpOpen = (ticket) => {
    setSelectedTicket(ticket);
    setShowRolesPopUp(true);
  };
  const rolesPopUpClose = () => {
    setShowRolesPopUp(false);
    getAllTickets();
  };

  const mesagesPopUpOpen = (ticket) => {
    setSelectedTicket(ticket);
    setMessagesOpen(true);
  };
  const mesagesPopUpClose = () => {
    setMessagesOpen(false);
    getAllTickets();
  };

  // Columns (kept consistent with your previous implementation)
  const columns = [
    {
      id: "ticketNumber",
      name: "Ticket Number",
      selector: (row) => row.ticketNumber,
      cell: (row) => (
        <div className="d-flex flex-column">
          <OverlayTrigger
            placement="bottom"
            delay={{ show: 250, hide: 400 }}
            overlay={renderTooltip(row.ticketNumber)}
          >
            <span>{row.ticketNumber}</span>
          </OverlayTrigger>
        </div>
      ),
      sortable: true,
      width: "150px",
    },
    {
      id: "date",
      name: "Date",
      selector: (row) =>
        Helpers.convertUtcToTimezone(row.createdAt, "M/D/YYYY"),
      cell: (row) => (
        <OverlayTrigger
          placement="bottom"
          delay={{ show: 250, hide: 400 }}
          overlay={renderTooltip(
            Helpers.convertUtcToTimezone(row.createdAt, "M/D/YYYY")
          )}
        >
          <span>
            {row.createdAt
              ? Helpers.convertUtcToTimezone(row.createdAt, "M/D/YYYY")
              : "--"}
          </span>
        </OverlayTrigger>
      ),
      reorder: true,
      sortable: true,
      style: { cursor: "text" },
      width: "120px",
    },
    {
      id: "category",
      name: "Category",
      selector: (row) => row.category,
      cell: (row) => {
        const text = row.category || "--";
        const displayText = text.length > 20 ? text.slice(0, 20) + "..." : text;
        return (
          <OverlayTrigger
            placement="bottom"
            delay={{ show: 250, hide: 400 }}
            overlay={renderTooltip(text)}
          >
            <span>{displayText}</span>
          </OverlayTrigger>
        );
      },
      sortable: true,
      width: "150px",
    },
    {
      id: "subject",
      name: "Subject",
      selector: (row) => row.subject,
      cell: (row) => {
        const text = row.subject || "--";
        const displayText = text.length > 30 ? text.slice(0, 30) + "..." : text;
        return (
          <OverlayTrigger
            placement="bottom"
            delay={{ show: 250, hide: 400 }}
            overlay={renderTooltip(text)}
          >
            <span
              onClick={() => mesagesPopUpOpen(row)}
              className="subjectTicket"
            >
              {displayText}
            </span>
          </OverlayTrigger>
        );
      },
      sortable: true,
      width: "150px",
    },
    {
      id: "message",
      name: "Message",
      selector: (row) => row.message,
      cell: (row) => {
        const text = row.message || "--";
        const displayText = text.length > 60 ? text.slice(0, 60) + "..." : text;
        return (
          <OverlayTrigger
            placement="bottom"
            delay={{ show: 250, hide: 400 }}
            overlay={renderTooltip(text)}
          >
            <span>{displayText}</span>
          </OverlayTrigger>
        );
      },
      sortable: true,
      width: "260px",
    },
    {
      id: "firstName",
      name: "Posted By",
      selector: (row) => row.firstName,
      cell: (row) => (
        <OverlayTrigger
          placement="bottom"
          delay={{ show: 250, hide: 400 }}
          overlay={renderTooltip(`${row.firstName} ${row.lastName}`)}
        >
          <span>{`${row.firstName} ${row.lastName}` || "--"}</span>
        </OverlayTrigger>
      ),
      sortable: true,
      width: "150px",
    },
    {
      id: "email",
      name: "Contact Detail",
      selector: (row) => row.email,
      cell: (row) => (
        <div className="d-flex flex-column">
          <OverlayTrigger
            placement="bottom"
            delay={{ show: 250, hide: 400 }}
            overlay={renderTooltip(row.email)}
          >
            <a
              href={`mailto:${row.email}`}
              className="d-flex align-items-center text-decoration-none"
            >
              <i className="mdi mdi-email text-primary me-1"></i>
              <span>
                {row.email.length > 20
                  ? row.email.slice(0, 20) + "..."
                  : row.email}{" "}
              </span>
            </a>
          </OverlayTrigger>

          <OverlayTrigger
            placement="bottom"
            delay={{ show: 250, hide: 400 }}
            overlay={renderTooltip(row.mobile)}
          >
            <div className="d-flex align-items-center">
              <i className="mdi mdi-phone text-success me-1"></i>
              <span>{row.mobile || "--"}</span>
            </div>
          </OverlayTrigger>
        </div>
      ),
      sortable: true,
      width: "230px",
    },
    {
      id: "Priority",
      name: "Priority",
      cell: (row) =>
        row.priority ? (
          <select
            className="form-select form-select-sm text-white"
            value={row.priority || "LOW"}
            disabled
            style={{
              backgroundColor:
                row.priority === "HIGH"
                  ? "#F44336" // Red
                  : row.priority === "MEDIUM"
                  ? "#FFC107" // Yellow
                  : "#4CAF50", // Green for LOW or default
              color: row.priority === "MEDIUM" ? "black" : "white", // contrast for orange
            }}
          >
            <option value="LOW">Low</option>
            <option value="MEDIUM">Medium</option>
            <option value="HIGH">High</option>
          </select>
        ) : (
          "--"
        ),
      sortable: false,
      width: "135px",
    },
    {
      id: "Status",
      name: "Status",
      cell: (row) => (
        <select
          className="form-select form-select-sm text-white"
          style={{
            backgroundColor:
              row.status === "NEW"
                ? "#52cdff"
                : row.status === "INPROGRESS"
                ? "#ffa725"
                : row.status === "CLOSED"
                ? "#9E9E9E"
                : "",
            color: row.status === "INPROGRESS" ? "black" : "white", // better text contrast
          }}
          value={row.status}
          onChange={(e) => handleStatusChange(row.id, e.target.value)}
        >
          <option value="NEW">New</option>
          <option value="INPROGRESS">In Progress</option>
          <option value="CLOSED">Closed</option>
        </select>
      ),
      sortable: false,
      width: "150px",
    },
    {
      id: "assignedTo",
      name: "Assigned To",
      selector: (row) => row.assignedTo,
      cell: (row) =>
        row.assignedTo ? (
          <div className="d-flex align-items-center gap-2">
            <OverlayTrigger
              placement="bottom"
              delay={{ show: 250, hide: 400 }}
              overlay={renderTooltip(row.assignedTo)}
            >
              <span
                style={{ cursor: "pointer", textDecoration: "underline" }}
                onClick={() => mesagesPopUpOpen(row)}
              >
                {row.assignedUserFirstName &&
                row.assignedUserLastName &&
                row.assignedUserEmail
                  ? `${row.assignedUserFirstName} ${row.assignedUserLastName} - ${row.assignedUserEmail}`
                  : row.assignedUserEmail
                  ? row.assignedUserEmail
                  : row.assignedTo
                  ? row.assignedTo
                  : "--"}
              </span>
            </OverlayTrigger>
            <button
              className="btn btn-sm btn-outline-primary p-1"
              onClick={() => {
                rolesPopUpOpen(row);
                setNewAssign(true);
              }}
            >
              <i
                className="mdi mdi-account-plus"
                title="Re-assign to other user"
              ></i>
            </button>
          </div>
        ) : (
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => rolesPopUpOpen(row)}
          >
            <i className="mdi mdi-account-tie"></i>
          </button>
        ),
      sortable: true,
      width: "220px",
    },
    {
      id: "action",
      name: "Action",
      button: true,
      cell: (row) => (
        <div className="d-flex align-items-center">
          <button className="font-size" onClick={() => handleShowDelete(row)}>
            <i
              className="mdi mdi-delete-forever text-md text-danger mx-2 font-size"
              title="Delete"
            ></i>
          </button>
        </div>
      ),
      width: "50px",
    },
  ];

  return (
    <div className="row ext-space">
      <div className="col-12">
        <div className="card mb-3 shadow-sm border-0">
          <div className="card-body p-3">
            <TicketListToolbar
              categoryFilter={categoryFilter}
              statusFilter={statusFilter}
              priorityFilter={priorityFilter}
              searchKey={searchKey}
              onFilterChange={handleFilterChange}
              onSearch={handleSearch}
              onClear={clearFilters}
              onSearchClick={() => getAllTickets(1, pageSize, searchKey)}
            />
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <ResponsiveDataList
              loading={LoadData}
              data={tickets}
              columns={columns}
              total={total}
              countPerPage={pageSize}
              isServerSide
              pagination
              sortServer
              pointerOnHover
              highlightOnHover
              dataFetchApi={(pageNo, size, _search, sortBy) =>
                getAllTickets(
                  pageNo,
                  size,
                  searchKey,
                  sortBy || {},
                )
              }
              cardGridColumns={{ xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
              renderCard={(row) => (
                <TicketCard
                  row={row}
                  onOpenMessages={mesagesPopUpOpen}
                  onAssign={rolesPopUpOpen}
                  onReassign={(r) => {
                    rolesPopUpOpen(r);
                    setNewAssign(true);
                  }}
                  onDelete={handleShowDelete}
                  onStatusChange={handleStatusChange}
                />
              )}
            />
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      {show && (
        <DeleteModel
          show={show}
          content={
            <>
              <h5 className="mt-4 fw-bold">Ticket Detail</h5>
              <div className="p-3 border rounded-3 bg-light shadow-sm">
                {selectedTicket && (
                  <>
                    <div className="mb-2">
                      <span className="fw-bold">Name:</span>{" "}
                      {selectedTicket.firstName} {selectedTicket.lastName}
                      {selectedTicket.email && (
                        <span className="text-muted">
                          {" "}
                          – {selectedTicket.email}
                        </span>
                      )}
                    </div>
                    <div className="mb-2">
                      <span className="fw-bold">Subject:</span>{" "}
                      {selectedTicket.subject}
                    </div>
                    <div className="mb-2">
                      <span className="fw-bold">Message:</span>{" "}
                      {selectedTicket.message}
                    </div>
                  </>
                )}
              </div>
            </>
          }
          handleClose={handleClose}
          DeleteData={deleteTicket}
        />
      )}

      {/* Assign roles popup */}
      {showRolesPopUp && (
        <TicketAccess
          show={showRolesPopUp}
          handleClose={rolesPopUpClose}
          selectedTicket={selectedTicket}
          newAssign={newAssign}
          setNewAssign={setNewAssign}
        />
      )}

      {/* Messages modal */}
      {messagesOpen && (
        <MessagesModal
          show={messagesOpen}
          handleClose={mesagesPopUpClose}
          selectedTicket={selectedTicket}
          setSelectedTicket={setSelectedTicket}
        />
      )}
    </div>
  );
};

export default Tickets;
